// src/components/ITextToolbar.js
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormGroup,
  TextField,
  FormControl,
  NativeSelect,
  InputLabel,
  OutlinedInput,
  Button,
  InputAdornment,
} from "@mui/material";
import { IText } from "fabric";
import { FontNames } from "./FontNames";
import { useData } from "../hooks/useData";
import useCanvasHistory from "../hooks/useCanvasHistory";

const ITextToolbar = ({ canvas }) => {
  const { canvasScale, setSnackbar, setSelectedObject } = useData();
  const { saveHistory } = useCanvasHistory(canvas);
  const [canvasText, setCanvasText] = useState("");
  const [fontSize, setFontSize] = useState(20);
  const [fontFamily, setFontFamily] = useState("Pacifico");
  const [strokeFactor, setStrokeFactor] = useState(0); // Stroke as fraction of fontSize

  // Load fonts to ensure they’re available
  useEffect(() => {
    const loadFonts = async () => {
      try {
        await Promise.all(
          FontNames.map((font) =>
            document.fonts.load(`16px "${font}"`).then(
              () => console.log(`ITextToolbar: Loaded font ${font}`),
              (err) =>
                console.warn(`ITextToolbar: Failed to load font ${font}`, err)
            )
          )
        );
        console.log("ITextToolbar: All fonts processed");
      } catch (error) {
        console.error("ITextToolbar: Error loading fonts", error);
      }
    };
    loadFonts();
  }, []);

  const handleTextChange = (e) => {
    setCanvasText(e.target.value);
  };

  const handleFontSize = (e) => {
    const fontSizeValue = parseInt(e.target.value) || 20;
    setFontSize(fontSizeValue);
  };

  const handleFontFamily = (e) => {
    setFontFamily(e.target.value);
  };

  const handleStrokeFactor = (e) => {
    const strokeValue = Math.max(0, parseFloat(e.target.value) || 0);
    setStrokeFactor(strokeValue);
  };

  const handleAddText = () => {
    if (!canvas || !canvasText || !canvasScale) {
      setSnackbar({
        open: true,
        message: "No text entered or canvas not initialized.",
        severity: "error",
      });
      console.error("ITextToolbar: Cannot add text", {
        canvas: !!canvas,
        canvasText,
        canvasScale,
      });
      return;
    }

    console.log("ITextToolbar: Checking IText", { IText });
    if (!IText) {
      console.error("ITextToolbar: IText is not defined");
      setSnackbar({
        open: true,
        message: "Failed to add text. Please try refreshing.",
        severity: "error",
      });
      return;
    }

    try {
      const scaledFontSize = fontSize;
      const strokeWidth = fontSize * strokeFactor; // Calculate strokeWidth
      const canvasWidth = canvas.getWidth();
      const canvasHeight = canvas.getHeight();
      const textOptions = {
        left: canvasWidth / 2,
        top: canvasHeight / 2,
        fontFamily: fontFamily,
        fontSize: scaledFontSize,
        fill: "#000000",
        stroke: strokeWidth > 0 ? "#000000" : null,
        strokeWidth: strokeWidth,
        strokeUniform: true,
        opacity: 1,
        shadow: {},
        originX: "center",
        originY: "center",
        scaleX: 1,
        scaleY: 1,
        selectable: true,
        evented: true,
      };
      console.log("ITextToolbar: Adding text", {
        text: canvasText,
        ...textOptions,
        canvasScale,
        canvasWidth,
        canvasHeight,
        backgroundColor: canvas.backgroundColor,
        viewportTransform: canvas.viewportTransform,
        zoom: canvas.getZoom(),
      });

      // Reset viewport and zoom
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      canvas.setZoom(1);

      const itext = new IText(canvasText, textOptions);
      // Fallback to Arial if font fails
      if (!document.fonts.check(`16px "${fontFamily}"`)) {
        console.warn(
          `ITextToolbar: Font ${fontFamily} not available, using Arial`
        );
        itext.set("fontFamily", "Arial");
      }
      canvas.add(itext);
      canvas.setActiveObject(itext);
      canvas.bringObjectToFront(itext);
      setSelectedObject(itext);
      canvas.requestRenderAll();
      saveHistory(canvas);

      const bounds = itext.getBoundingRect();
      console.log("ITextToolbar: Text added", {
        text: itext.text,
        fontFamily: itext.fontFamily,
        fontSize: itext.fontSize,
        strokeWidth: itext.strokeWidth,
        left: itext.left,
        top: itext.top,
        fill: itext.fill,
        stroke: itext.stroke,
        canvasWidth,
        canvasHeight,
        backgroundColor: canvas.backgroundColor,
        stackIndex: canvas.getObjects().indexOf(itext),
        objectCount: canvas.getObjects().length,
        bounds: {
          left: bounds.left,
          top: bounds.top,
          width: bounds.width,
          height: bounds.height,
        },
        isVisible:
          bounds.left >= 0 &&
          bounds.top >= 0 &&
          bounds.left + bounds.width <= canvasWidth &&
          bounds.top + bounds.height <= canvasHeight,
      });
      setSnackbar({
        open: true,
        message: `Text "${canvasText}" added.`,
        severity: "success",
      });
    } catch (error) {
      console.error("ITextToolbar: Failed to add text", error);
      setSnackbar({
        open: true,
        message: "Error adding text: " + error.message,
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          color: "#0d2445",
          fontFamily: "Roboto",
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        Enter Text
      </Typography>
      <Box sx={{ textAlign: "center" }}>
        <FormGroup>
          <TextField
            type="text"
            id="canvasText"
            multiline
            margin="normal"
            InputProps={{ sx: { bgcolor: "#fff" } }}
            label="Text"
            value={canvasText}
            onChange={handleTextChange}
            variant="outlined"
          />
          <TextField
            type="number"
            id="fontSize"
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
              sx: { bgcolor: "#fff", paddingLeft: 1 },
            }}
            label="Size"
            value={fontSize}
            onChange={handleFontSize}
            variant="outlined"
          />
          <TextField
            type="number"
            id="strokeFactor"
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">x</InputAdornment>,
              sx: { bgcolor: "#fff", paddingLeft: 1 },
              inputProps: { min: 0, max: 0.2, step: 0.01 },
            }}
            label="Stroke Factor"
            value={strokeFactor}
            onChange={handleStrokeFactor}
            variant="outlined"
          />
          <FormControl
            fullWidth
            variant="outlined"
            sx={{ marginTop: "20px", minWidth: "200px", marginBottom: "12px" }}
          >
            <InputLabel sx={{ color: "#5e5e5e" }} htmlFor="fontstyle">
              Font Style
            </InputLabel>
            <NativeSelect
              sx={{
                paddingLeft: 2,
                "&.MuiInputBase-formControl": { bgcolor: "#fff" },
              }}
              inputProps={{ id: "fontstyle" }}
              value={fontFamily}
              label="Font Style"
              input={<OutlinedInput label="Font Style" />}
              onChange={handleFontFamily}
            >
              {FontNames.map((option, i) => (
                <option key={i} value={option} style={{ fontFamily: option }}>
                  {option}
                </option>
              ))}
            </NativeSelect>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleAddText}
            sx={{ marginTop: "8px" }}
            disabled={!canvasText}
          >
            Add Text
          </Button>
        </FormGroup>
      </Box>
    </Box>
  );
};

export default ITextToolbar;
